import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import type {
  UserCompanyMemberDto,
  PaginatedResultDto,
  InviteUserToCompanyDto,
  UpdateUserCompanyDto,
  ValidatorUserDto,
} from '@monorepo/shared';
import { apiClient } from '../lib/axios';
import { queryClient } from '../lib/queryClient';
import { invalidateDocumentWorkflow } from './documentInvalidation';

const usersApi = {
  getCompanyUsers: async (
    companyId: string,
    page: number = 1,
    limit: number = 10,
    search?: string,
    role?: string,
  ): Promise<PaginatedResultDto<UserCompanyMemberDto>> => {
    const params: Record<string, string | number> = { page, limit };
    if (search) params['search'] = search;
    if (role) params['role'] = role;

    const { data: response } = await apiClient.get<PaginatedResultDto<UserCompanyMemberDto>>(
      `/companies/${companyId}/users`,
      { params },
    );
    return response;
  },

  inviteUser: async (
    companyId: string,
    data: InviteUserToCompanyDto,
  ): Promise<UserCompanyMemberDto> => {
    const { data: response } = await apiClient.post<UserCompanyMemberDto>(
      `/companies/${companyId}/users/invite`,
      data,
    );
    return response;
  },

  updateUserRole: async (
    companyId: string,
    userCompanyId: string,
    data: UpdateUserCompanyDto,
  ): Promise<UserCompanyMemberDto> => {
    const { data: response } = await apiClient.patch<UserCompanyMemberDto>(
      `/companies/${companyId}/users/${userCompanyId}`,
      data,
    );
    return response;
  },

  removeUser: async (companyId: string, userCompanyId: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/users/${userCompanyId}`);
  },

  resendInvite: async (companyId: string, userCompanyId: string): Promise<void> => {
    await apiClient.post(`/companies/${companyId}/users/${userCompanyId}/resend-invite`);
  },

  getCompanyValidators: async (companyId: string): Promise<ValidatorUserDto[]> => {
    const { data: response } = await apiClient.get<ValidatorUserDto[]>(
      `/companies/${companyId}/users/validators`,
    );
    return response;
  },
};

export const useGetCompanyUsers = (
  companyId: string,
  page: number = 1,
  limit: number = 10,
  search?: string,
  role?: string,
) => {
  return useQuery({
    queryKey: ['company-users', companyId, page, limit, search, role],
    queryFn: () => usersApi.getCompanyUsers(companyId, page, limit, search, role),
    enabled: !!companyId,
  });
};

export const useInviteUser = (companyId: string) => {
  return useMutation({
    mutationFn: (data: InviteUserToCompanyDto) => usersApi.inviteUser(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
      queryClient.invalidateQueries({ queryKey: ['company-validators', companyId] });
      toast.success(i18next.t('admin.users.invite_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useUpdateUserRole = (companyId: string) => {
  return useMutation({
    mutationFn: ({ userCompanyId, data }: { userCompanyId: string; data: UpdateUserCompanyDto }) =>
      usersApi.updateUserRole(companyId, userCompanyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
      queryClient.invalidateQueries({ queryKey: ['company-validators', companyId] });
      queryClient.invalidateQueries({ queryKey: ['company-group', companyId] });
      toast.success(i18next.t('admin.users.update_role_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useRemoveUser = (companyId: string) => {
  return useMutation({
    mutationFn: (userCompanyId: string) => usersApi.removeUser(companyId, userCompanyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
      queryClient.invalidateQueries({ queryKey: ['company-validators', companyId] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          (query.queryKey[0] === 'company-groups' || query.queryKey[0] === 'company-group') &&
          query.queryKey[1] === companyId,
      });
      invalidateDocumentWorkflow(companyId);
      toast.success(i18next.t('admin.users.remove_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useResendInvite = (companyId: string) => {
  return useMutation({
    mutationFn: (userCompanyId: string) => usersApi.resendInvite(companyId, userCompanyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
      toast.success(i18next.t('admin.users.resend_invite_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useGetCompanyValidators = (companyId: string) => {
  return useQuery({
    queryKey: ['company-validators', companyId],
    queryFn: () => usersApi.getCompanyValidators(companyId),
    enabled: !!companyId,
  });
};
